import { useCallback, useRef } from "react";
import { useErrorHandling } from "../../hooks/useErrorHandling";
import { useLabels } from "../../hooks/useLabels";
import { useSnackbar } from "../../hooks/useSnackbar";
import { labels } from "./labels";
import { useAppContext } from "../../hooks/useAppContext";

// TODO: share with desktop creator
const waitForPartnerConnectWindowCloseAsync = (
	window: Window,
	delay = 100,
	counter = 1,
	leftDomain = false,
	maxDelay = 60000,
): Promise<boolean> => {
	let factor;
	if (counter % 10 === 0) {
		factor = counter / 10;
		delay = 100 * factor;
	}

	if (delay > maxDelay) {
		delay = maxDelay;
	}

	counter += 1;

	return eXpress.utils.waitAsync(delay).then(() => {
		try {
			if (
				(window && window.closed) ||
				!window ||
				(window &&
					window.document &&
					// eslint-disable-next-line @typescript-eslint/no-deprecated
					window.document.domain === document.domain &&
					leftDomain)
			) {
				// The first page in the connect window is from smartphoto with a 'Connect' button
				// https://smartphoto-be-qa.stgr.cloud/nl/producten/locatephotosgooglephotosux
				// Clicking connect redirects to a partner login OAuth page
				// After Connect is clicked: leftDomain will be through
				// I don't know why this info is needed?
				return Promise.resolve(leftDomain);
			}

			return waitForPartnerConnectWindowCloseAsync(window, delay, counter, leftDomain);
		} catch (e) {
			// After redirect to partner login OAuth page, we get errors when accessing document
			// Failed to read a named property 'document' from 'Window': Blocked a frame with origin
			if (e instanceof Error && e.name === "SecurityError") {
				return waitForPartnerConnectWindowCloseAsync(window, delay, counter, true);
			}

			return Promise.resolve(true);
		}
	});
};

/**
 * Returns a function that show the popup to connect a partner
 */
export const usePartnerConnectWindow = () => {
	const partnerWindow = useRef<Window | null>(null);

	const { createErrorHandler } = useErrorHandling();
	const { addSnackbar } = useSnackbar();
	const {
		platform: { popup },
	} = useAppContext();

	const [[cantOpenPartnerWindowLabel]] = useLabels([labels.cantOpenPartnerWindow]);

	return useCallback(
		async (photoStore: Pick<eXpress.core.IPhotoStore, "loginUrl" | "name">) => {
			if (!photoStore.loginUrl) {
				const errorHandler = createErrorHandler();
				errorHandler(
					new eXpress.Warning(
						"Failed to open partner window",
						`No loginUrl available for partner '${photoStore.name}'`,
					),
				);
				return false;
			}

			try {
				partnerWindow.current = popup.open(photoStore.loginUrl, {
					target: "partner-window",
				});
			} catch {
				partnerWindow.current = null;
			}

			if (!partnerWindow.current) {
				addSnackbar({
					id: "partner-window-failed",
					text: cantOpenPartnerWindowLabel,
				});
				const errorHandler = createErrorHandler();
				errorHandler(
					new eXpress.Warning(
						"Failed to open partner window",
						"Can't open partner window, probably blocked by popup blocker. Toast is displayed to the user.",
					),
				);

				return false;
			}

			return waitForPartnerConnectWindowCloseAsync(partnerWindow.current);
		},
		[addSnackbar, cantOpenPartnerWindowLabel, createErrorHandler, popup],
	);
};
